import { type ComponentProps } from 'react';

import { formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { type DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';

/** Available date display formats */
export type DateFormat =
  | 'digit' // "01/01/2025"
  | 'digit-time' // "01/01/2025 14:30"
  | 'datetime-at' // "January 1, 2025 at 14:30"
  | 'iso-short' // "2025-01-01 14:30"
  | 'relative'; // "2 hours ago", "in 3 days"

/** Single date input type */
type SingleDateInput = Date | number | string;

/** Date input - can be a single date or a date range */
type DateInput = SingleDateInput | DateRange;

// Format pattern mapping. 24h everywhere: never the locale time tokens (p/pp)
// or h/hh/a, which render AM/PM.
const formatPatterns: Record<Exclude<DateFormat, 'relative'>, string> = {
  digit: 'dd/MM/yyyy',
  'digit-time': 'dd/MM/yyyy HH:mm',
  'datetime-at': "PPP 'at' HH:mm",
  'iso-short': 'yyyy-MM-dd HH:mm',
};

// Check if input is a DateRange
function isDateRange(date: DateInput): date is DateRange {
  return (
    typeof date === 'object' &&
    !(date instanceof Date) &&
    ('from' in date || 'to' in date)
  );
}

// Normalize single date input
function normalizeDate(date: SingleDateInput): Date {
  if (date instanceof Date) return date;
  if (typeof date === 'number') return new Date(date);
  if (typeof date === 'string') return parseISO(date);
  return new Date(NaN);
}

export interface DateDisplayProps
  extends Omit<ComponentProps<'time'>, 'dateTime'> {
  /** The date to display - accepts Date, number (timestamp), ISO string, or DateRange */
  date: DateInput;
  /** Display format preset */
  displayFormat?: DateFormat;
  /** Custom format string (overrides displayFormat) */
  formatString?: string;
  /** Fallback text when date is invalid */
  fallback?: string;
  /** Include suffix for relative format (e.g., "ago" or "in X") */
  addSuffix?: boolean;
  /** Timezone to display the date in (defaults to browser timezone) */
  timezone?: string;
  /** Whether to append the offset for the displayed date, e.g. "(GMT+02:00)" */
  showOffset?: boolean;
  /** Separator for date ranges (default: " → ") */
  rangeSeparator?: string;
}

export const DateDisplay = ({
  date,
  displayFormat = 'iso-short',
  formatString,
  fallback = '-',
  addSuffix = true,
  timezone,
  showOffset = false,
  rangeSeparator = ' → ',
  className,
  ...props
}: DateDisplayProps) => {
  // Use provided timezone or default to browser timezone
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  // One call does the zone shift and the offset, so both always agree on the
  // reference date. ponytail: no offset on 'relative' — "5 minutes ago" has no wall clock.
  const formatSingleDate = (singleDate: Date): string =>
    displayFormat === 'relative'
      ? formatDistanceToNow(singleDate, { addSuffix })
      : formatInTimeZone(
          singleDate,
          tz,
          (formatString ?? formatPatterns[displayFormat]) +
            (showOffset ? ' (OOOO)' : ''),
        );

  // Handle DateRange
  if (isDateRange(date)) {
    const fromDate = date.from ? normalizeDate(date.from) : null;
    const toDate = date.to ? normalizeDate(date.to) : null;

    // If no valid dates in range, show fallback
    if ((!fromDate || !isValid(fromDate)) && (!toDate || !isValid(toDate))) {
      return <span className={className}>{fallback}</span>;
    }

    // Only from date
    if (fromDate && isValid(fromDate) && (!toDate || !isValid(toDate))) {
      return (
        <time
          dateTime={fromDate.toISOString()}
          className={cn(className)}
          {...props}
        >
          {formatSingleDate(fromDate)}
        </time>
      );
    }

    // Both from and to dates
    if (fromDate && isValid(fromDate) && toDate && isValid(toDate)) {
      return (
        <span className={cn(className)} {...props}>
          <time dateTime={fromDate.toISOString()}>
            {formatSingleDate(fromDate)}
          </time>
          {rangeSeparator}
          <time dateTime={toDate.toISOString()}>
            {formatSingleDate(toDate)}
          </time>
        </span>
      );
    }

    return <span className={className}>{fallback}</span>;
  }

  // Handle single date
  const normalizedDate = normalizeDate(date);
  if (!isValid(normalizedDate)) {
    return <span className={className}>{fallback}</span>;
  }

  return (
    <time
      dateTime={normalizedDate.toISOString()}
      className={cn(className)}
      {...props}
    >
      {formatSingleDate(normalizedDate)}
    </time>
  );
};
